import { Fragment, ReactElement, useRef, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import {
  FormValidatorType,
  createForm,
  useCreateFormStore,
  useFormContext,
} from '@components/form';
import { FormInstance } from '@components/form/create-form';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconProps } from '@components/icons';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconDirectionDown } from '@components/icons/direction-down';
import { IconDirectionUp } from '@components/icons/direction-up';
import { IconEquals } from '@components/icons/equals';
import { IconEqualsLess } from '@components/icons/equals-less';
import { IconEqualsMore } from '@components/icons/equals-more';
import { IconEqualsNot } from '@components/icons/equals-not';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconTrash } from '@components/icons/trash';
import { InfoBox } from '@components/info-box';
import { ComparatorValueContainer } from '@components/integrations/condition';
import { useListOptions } from '@components/integrations/lists';
import { SelectOption } from '@components/select';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import {
  FieldMappingFieldItem,
  FieldMappingGroup,
  VariableValue,
} from '@components/text-editors/addons/field-mapping';
import { TextField } from '@components/text-field';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { uuid } from '@utils/uuid';

import {
  Condition,
  ConditionType,
  JourneyActionDetailed,
  JourneyType,
  TravellerJourneyActionDetailed,
} from 'api/types';
import { ActionFooter, useVariableGroups } from 'ui/journey-builder/node-types/common';

import { BaseContentProps, InfoBoxVariant } from '../common';
import { getFieldMappingId } from '../helpers';

type InfoType = 'deprecatedAlert';

const TYPE_TO_VARIANT: Record<InfoType, InfoBoxVariant> = {
  deprecatedAlert: 'orange',
};

const TYPE_TO_TEXT: Record<InfoType, keyof typeof locale.en> = {
  deprecatedAlert: 'deprecatedAlert',
};

const TYPE_TO_ICON: Record<InfoType, ReactElement<IconProps>> = {
  deprecatedAlert: <IconFormIndicatorWarning />,
};

export const ConditionInfoBox = ({
  infoType,
  children,
}: {
  infoType: InfoType;
  children?: ReactElement;
}) => {
  const { t } = useScopedTranslate(locale);
  const variant = TYPE_TO_VARIANT[infoType];
  const text = t(TYPE_TO_TEXT[infoType]);
  const icon = TYPE_TO_ICON[infoType];
  return <InfoBox variant={variant} text={text} icon={icon} children={children} />;
};

type Data = {
  description: string;
  conditions: Condition[];
  conditionType: ConditionType;
  loopAction: string;
};

export const useConjunctionLabel = (conditionType: ConditionType) => {
  const { t } = useScopedTranslate(locale);
  return conditionType === 'all' ? t('core.and') : t('core.or');
};

const Form = createForm<Data>();

type AvailableActionTypes = 'break_loop' | 'condition';

type ConditionFormContentProps<T extends AvailableActionTypes> = BaseContentProps<
  JourneyActionDetailed<T> | TravellerJourneyActionDetailed<T>
> & {
  hasDeprecatedAlert?: boolean;
  onSubmit({
    conditions,
    conditionType,
  }: {
    conditionType: ConditionType;
    conditions: Condition[];
  }): Promise<any>;
  onCancel(): void;
  journeyId?: JourneyType['shortId'];
  conditionDescription: string;
  hideActionButtons?: boolean;
};

export const ConditionFormContent = <T extends AvailableActionTypes>({
  action,
  onSubmit,
  onCancel,
  journeyId = '',
  hasDeprecatedAlert = false,
  conditionDescription,
  hideActionButtons,
}: ConditionFormContentProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const fieldMappingId = getFieldMappingId(journeyId);

  const conditionTypes: SelectOption[] = [
    { value: 'all', label: t('all') },
    { value: 'any', label: t('any') },
  ];
  const initialConditions = useRef(action.values?.conditions ?? []);
  const [conditions, setConditions] = useState<Condition[]>(
    initialConditions.current.map((condition) => ({ ...condition, id: condition.id ?? uuid() }))
  );
  const isDirty = formStore.useIsDirty() || initialConditions.current.length !== conditions.length;

  const baseVariableGroups = useVariableGroups({
    actionVariables: action?.actionVariables,
    journeyId,
    types: [
      'email',
      'text',
      'options',
      'phone',
      'date',
      'separator',
      'boolean',
      'select',
      'multi_select',
    ],
  });

  const formValues = formStore.useFormValues();
  const conditionType = formValues.conditionType as ConditionType;

  const handleSubmit = () => {
    const formConditions = conditions.map((condition) => {
      const { elementId, comparatorId, valueId } = getComparatorIds(condition.id);
      return {
        id: condition.id,
        field: formValues[elementId],
        comparator: formValues[comparatorId],
        value: formValues[valueId],
      } as Condition;
    });
    return onSubmit({ conditions: formConditions, conditionType });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSubmit}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <Text font="regular14" text={t('conditionDescription', { conditionDescription })} />
        </HideContainer>
        <Sidebar.Separator />
        {hasDeprecatedAlert && (
          <>
            <ConditionInfoBox infoType="deprecatedAlert" />
            <Sidebar.Separator />
          </>
        )}
        <HideContainer title={t('actionConditions')}>
          <Stack space={6}>
            <Stack orientation="horizontal" justify="space-between" align="center">
              <Text
                font="regular14"
                text={t('conditionDescriptionType', { conditionType, conditionDescription })}
              />
              <SelectContainer>
                <Form.Select
                  label=""
                  id="conditionType"
                  options={conditionTypes}
                  isClearable={false}
                  defaultValue={action.values?.conditionType ?? 'all'}
                />
              </SelectContainer>
            </Stack>
            <Stack space={4}>
              {conditions.map((condition, idx) => (
                <Fragment key={condition.id}>
                  {idx > 0 && (
                    <Stack space={2} orientation="horizontal" justify="center" align="center">
                      <Separator />
                      <Text
                        font="semibold14"
                        style={{ textTransform: 'uppercase' }}
                        color="textSecondary"
                        text={conditionType === 'all' ? t('core.and') : t('core.or')}
                      />
                      <Separator />
                    </Stack>
                  )}
                  <ConditionComponent
                    key={condition.id}
                    variableGroups={baseVariableGroups}
                    onRemove={() => setConditions(conditions.filter((c) => c.id !== condition.id))}
                    condition={condition}
                    form={Form}
                    index={idx}
                    fieldMappingId={fieldMappingId}
                  />
                </Fragment>
              ))}
              <Stack orientation="horizontal">
                <Button
                  onClick={() =>
                    setConditions([
                      ...conditions,
                      {
                        id: uuid(),
                        field: { value: '' },
                        comparator: undefined,
                        value: undefined,
                      },
                    ])
                  }
                  text={t('addElementLabel')}
                  iconLeft={<IconAddPlus />}
                  variant="secondary"
                />
              </Stack>
            </Stack>
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
      </Stack>
      <ActionFooter
        action={action}
        isDisabled={!isDirty || conditions.length === 0}
        hideActionButtons={hideActionButtons}
        onCancel={onCancel}
        form={Form}
      />
    </FormWithUnsavedChangesPrompt>
  );
};

const SelectContainer = styled.div({
  width: pxToRem(96),
});

type ConditionComponentProps<T> = {
  variableGroups: FieldMappingGroup[];
  condition: Condition;
  onRemove: () => void;
  form: FormInstance<T>;
  isWebhook?: boolean;
  index?: number;
  fieldMappingId: string;
};

export const ConditionComponent = ({
  variableGroups,
  condition,
  onRemove,
  form: Form,
  isWebhook,
  index,
  fieldMappingId,
}: ConditionComponentProps<Data>) => {
  const { t } = useScopedTranslate(locale);

  const { elementId } = getComparatorIds(condition.id);

  const { store } = useFormContext();
  const element = store.useFieldValue(elementId) as VariableValue;

  return (
    <>
      <Card space={2}>
        <Stack space={2}>
          <Stack space={2} orientation="horizontal">
            <div style={{ flexGrow: 1 }}>
              <Form.FieldMappingField
                id={elementId}
                label={t('elementLabel')}
                variableGroups={variableGroups}
                defaultValue={condition.field}
                validators={['required']}
                singleLine
                plaintext
                fieldMappingId={fieldMappingId}
              />
            </div>
            <IconButton
              icon={<IconTrash />}
              variant="secondary-purple"
              onClick={onRemove}
              style={{ marginTop: pxToRem(28) }}
            />
          </Stack>
          {element?.value && (
            <Comparator
              form={Form}
              condition={condition}
              variableGroups={variableGroups}
              isWebhook={isWebhook}
              index={index}
            />
          )}
        </Stack>
      </Card>
    </>
  );
};

export const getComparatorIds = (id: string) => ({
  elementId: `${id}Element` as keyof Data,
  comparatorId: `${id}Comparator` as keyof Data,
  valueId: `${id}Value` as keyof Data,
});

const Comparator = ({
  condition,
  form: Form,
  variableGroups,
  isWebhook = false,
  index,
}: {
  condition: Condition;
  form: FormInstance<Data>;
  variableGroups: FieldMappingGroup[];
  isWebhook?: boolean;
  index?: number;
}) => {
  const { t } = useScopedTranslate(locale);
  const { store } = useFormContext();
  const formValues = store.useFormValues();
  const BASE_COMPARATORS: SelectOption[] = [
    {
      value: 'eq',
      label: (
        <Stack orientation="horizontal" space={3}>
          <IconEquals />
          {t('is')}
        </Stack>
      ),
    },
    {
      value: 'neq',
      label: (
        <Stack orientation="horizontal" space={3}>
          <IconEqualsNot />
          {t('isNot')}
        </Stack>
      ),
    },
  ];
  const BOOLEAN_COMPARATORS: SelectOption[] = [
    {
      value: 'boolean_true',
      label: (
        <Stack orientation="horizontal" space={3}>
          <IconEquals />
          {t('isTrue')}
        </Stack>
      ),
    },
    {
      value: 'boolean_false',
      label: (
        <Stack orientation="horizontal" space={3}>
          <IconEqualsNot />
          {t('isFalse')}
        </Stack>
      ),
    },
  ];
  const DATE_COMPARATORS: SelectOption[] = [
    {
      value: 'date_lt',
      label: (
        <Stack orientation="horizontal" space={3}>
          <IconDirectionUp />
          {t('before')}
        </Stack>
      ),
    },
    {
      value: 'date_lte',
      label: (
        <Stack orientation="horizontal" space={3}>
          <IconEqualsMore />
          {t('isOrBefore')}
        </Stack>
      ),
    },
    {
      value: 'date_eq',
      label: (
        <Stack orientation="horizontal" space={3}>
          <IconEquals />
          {t('is')}
        </Stack>
      ),
    },
    {
      value: 'date_neq',
      label: (
        <Stack orientation="horizontal" space={3}>
          <IconEqualsNot />
          {t('isNot')}
        </Stack>
      ),
    },
    {
      value: 'date_gte',
      label: (
        <Stack orientation="horizontal" space={3}>
          <IconEqualsLess />
          {t('isOrAfter')}
        </Stack>
      ),
    },
    {
      value: 'date_gt',
      label: (
        <Stack orientation="horizontal" space={3}>
          <IconDirectionDown />
          {t('after')}
        </Stack>
      ),
    },
  ];
  const { elementId, comparatorId, valueId } = getComparatorIds(condition.id);
  const validators = ['required'] as FormValidatorType[];

  const findMappedField = (): FieldMappingFieldItem | undefined => {
    const value = formValues[elementId as keyof typeof formValues] as VariableValue;
    if (!value || !value.variables) {
      return undefined;
    }
    const variable = Object.values(value.variables)[0];
    if (variable?.source === 'action') {
      const action = variableGroups.find((group) => group.id === variable.action);
      if (action) {
        return action.fields.find((field) => field.field === variable.field);
      }
    } else {
      return variableGroups
        .flatMap((obj) => obj.fields)
        .find((field) => field.source === variable?.source && field.field === variable?.field);
    }
  };

  const mappedField = findMappedField();
  const listOptions = useListOptions(mappedField?.optionsFromListId, mappedField?.excludedOptions);

  const isSelectType = mappedField?.type === 'select' || mappedField?.type === 'multi_select';

  const findValueType = () => {
    if (isSelectType) {
      return 'dropdown';
    } else if (mappedField?.type === 'date') {
      return 'date';
    } else if (mappedField?.type === 'boolean') {
      return 'boolean';
    }
    return 'text';
  };

  const valueType = findValueType();

  const findValueOptions = () => {
    if (mappedField?.options && mappedField.options.length > 0) {
      return mappedField.options.map((option) => {
        const label = 'display' in option ? option.display : option.label;
        const tooltip = typeof label === 'string' ? label : undefined;
        return {
          value: option.value,
          label,
          tooltip,
        };
      });
    }
    if (listOptions.length > 0) {
      return listOptions;
    }
    return [];
  };

  const valueOptions = findValueOptions();
  const [allowEmpty, setAllowEmpty] = useState(isWebhook && !condition.value);
  const WebhookAllowEmpty = () =>
    isWebhook ? (
      <Switch
        label={t('fieldmapInJourney')}
        key={`allow-${String(allowEmpty)}`}
        onChange={() => setAllowEmpty(!allowEmpty)}
        defaultValue={allowEmpty}
      />
    ) : null;

  const EmptyDisabledInput = () => <TextField label={t('valueLabel')} isDisabled />;

  switch (valueType) {
    case 'date':
      return (
        <Stack space={4}>
          <Stack space={2}>
            <Form.Select
              id={comparatorId}
              options={DATE_COMPARATORS}
              label={t('comparatorLabel')}
              defaultValue={condition.comparator}
              validators={validators}
              isClearable={false}
            />
            {allowEmpty ? (
              <EmptyDisabledInput />
            ) : (
              <Form.DatePicker
                id={valueId}
                label={t('dateLabel')}
                validators={validators}
                defaultValue={(condition.value as string) || undefined}
              />
            )}
          </Stack>
          <WebhookAllowEmpty />
        </Stack>
      );
    case 'dropdown':
      return (
        <Stack space={4}>
          <ComparatorValueContainer>
            <Form.Select
              id={comparatorId}
              options={BASE_COMPARATORS}
              label={t('comparatorLabel')}
              defaultValue={condition.comparator}
              validators={validators}
              isClearable={false}
            />
            {allowEmpty ? (
              <EmptyDisabledInput />
            ) : (
              <Form.Select
                selection="full"
                id={valueId}
                label={t('valueLabel')}
                options={valueOptions}
                defaultValue={condition.value as SelectOption}
                validators={validators}
                isClearable={false}
              />
            )}
          </ComparatorValueContainer>
          <WebhookAllowEmpty />
        </Stack>
      );
    case 'boolean':
      return (
        <ComparatorValueContainer>
          <Form.Select
            id={comparatorId}
            options={BOOLEAN_COMPARATORS}
            label={t('comparatorLabel')}
            defaultValue={condition.comparator}
          />
        </ComparatorValueContainer>
      );
    default:
      return (
        <Stack space={4}>
          <ComparatorValueContainer>
            <Form.Select
              id={comparatorId}
              options={BASE_COMPARATORS}
              label={t('comparatorLabel')}
              defaultValue={condition.comparator}
              validators={validators}
              isClearable={false}
            />
            {allowEmpty ? (
              <EmptyDisabledInput />
            ) : (
              <Form.TextField
                id={valueId}
                label={t('valueLabel')}
                validators={validators}
                defaultValue={condition.value as string}
                dataTestid={`${valueType}-condition-value-${index}`}
              />
            )}
          </ComparatorValueContainer>
          <WebhookAllowEmpty />
        </Stack>
      );
  }
};

const locale = {
  en: {
    deprecatedAlert:
      'We have a new version of the Condition action out! If you want to use the newest version, please re-add the action to your journey!',
    conditionDescription: '{conditionDescription} when specific conditions are met',
    conditionDescriptionType: '{conditionDescription} if {conditionType} of the following matches',
    addElementLabel: 'Add element',
    actionDetails: 'Action details',
    actionConditions: 'Conditions',
    all: 'All',
    any: 'Any',
    elementLabel: 'Element',
    comparatorLabel: 'Comparator',
    valueLabel: 'Value',
    dateLabel: 'Date',
    is: 'Is',
    isNot: 'Is not',
    isTrue: 'Is true',
    isFalse: 'Is false',
    before: 'Before',
    isOrBefore: 'Is or before',
    isOrAfter: 'Is or after',
    after: 'After',
    fieldmapInJourney: 'Select value in journey builder',
  },
};
